import { forgotPasswordRequestSchema, PASSWORD_RESET_RESEND_SECONDS } from '@fluxus/shared';
import { ArrowLeft, MailCheck } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router';
import { AuthScreen } from '@/components/layout/AuthScreen';
import { Button } from '@/components/ui/button';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { errorMessage } from '@/lib/api/errors';
import { useAuth } from '@/lib/auth/session';

/** "Esqueci minha senha" (RF-02): pede o link por e-mail. A resposta não revela se a conta existe. */
export function ForgotPasswordPage() {
  const { client } = useAuth();
  const location = useLocation();
  const initialEmail = (location.state as { email?: unknown } | null)?.email;
  const [email, setEmail] = useState(typeof initialEmail === 'string' ? initialEmail : '');
  const [fieldError, setFieldError] = useState<string>();
  const [formError, setFormError] = useState<unknown>(null);
  const [sentTo, setSentTo] = useState<{ email: string; message: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [wait, setWait] = useState(0);
  const emailRef = useRef<HTMLInputElement>(null);
  const resultRef = useRef<HTMLHeadingElement>(null);
  const ids = { email: useId(), alert: useId() };

  useEffect(() => {
    if (wait <= 0) return;
    const timer = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(timer);
  }, [wait]);

  useEffect(() => {
    if (sentTo) resultRef.current?.focus();
  }, [sentTo]);

  async function send(target: string) {
    setFormError(null);
    setSubmitting(true);
    try {
      const message = await client.requestPasswordReset(target);
      setSentTo({ email: target, message });
      setWait(PASSWORD_RESET_RESEND_SECONDS);
    } catch (err) {
      setFormError(err);
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    const parsed = forgotPasswordRequestSchema.safeParse({ email });
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message);
      emailRef.current?.focus();
      return;
    }
    setFieldError(undefined);
    await send(parsed.data.email);
  }

  if (sentTo) {
    return (
      <AuthScreen>
        <span className="inline-flex size-12 items-center justify-center rounded-full bg-status-green-bg text-status-green-fg">
          <MailCheck className="size-6" aria-hidden />
        </span>
        <h1
          ref={resultRef}
          tabIndex={-1}
          className="mt-5 text-3xl font-extrabold tracking-tight outline-none"
        >
          Confira seu e-mail
        </h1>
        <p className="mt-3 text-sm text-muted-foreground" role="status">
          {sentTo.message}
        </p>
        <p className="mt-3 text-sm text-muted-foreground">
          Enviamos para <strong className="break-all text-foreground">{sentTo.email}</strong>. O link vale por
          tempo limitado e só pode ser usado uma vez. Não chegou? Confira a caixa de spam.
        </p>
        {formError ? (
          <div className="mt-5">
            <FormAlert tone="error">{errorMessage(formError)}</FormAlert>
          </div>
        ) : null}
        <div className="mt-8 flex flex-col gap-3">
          <Button
            variant="outline"
            disabled={wait > 0}
            loading={submitting}
            onClick={() => void send(sentTo.email)}
          >
            {wait > 0 ? `Enviar de novo em ${wait} s` : 'Enviar de novo'}
          </Button>
          <Button asChild variant="ghost">
            <Link to="/entrar">
              <ArrowLeft aria-hidden />
              Voltar para entrar
            </Link>
          </Button>
        </div>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen>
      <h1 className="text-3xl font-extrabold tracking-tight">Esqueci minha senha</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Informe o e-mail da sua conta. Se ele estiver cadastrado, enviaremos um link para você criar uma nova
        senha.
      </p>
      <form
        noValidate
        onSubmit={onSubmit}
        className="mt-8 flex flex-col gap-5"
        aria-describedby={formError ? ids.alert : undefined}
      >
        <FormField id={ids.email} label="E-mail" error={fieldError}>
          <Input
            ref={emailRef}
            {...fieldA11y(ids.email, fieldError)}
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            autoFocus
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setFieldError(undefined);
            }}
            className="h-11"
          />
        </FormField>
        {formError ? (
          <FormAlert id={ids.alert} tone="error">
            {errorMessage(formError)}
          </FormAlert>
        ) : null}
        <Button type="submit" size="lg" className="mt-1 w-full" loading={submitting}>
          Enviar link
        </Button>
        <Button asChild variant="ghost">
          <Link to="/entrar">
            <ArrowLeft aria-hidden />
            Voltar para entrar
          </Link>
        </Button>
      </form>
    </AuthScreen>
  );
}
